/**
 * Bot AI for single-player Durak
 * Implements heuristic-based decision making for attack and defense
 */

import type { Card, Suit, PlaySlot } from '../types/game';
import {
  cardBeats,
  findOptimalDefense,
  getValidAttackCards,
  getOpenSlots,
} from './durak';
import { sortHand, isTrump } from './deck';

/**
 * Bot difficulty levels
 */
export type BotDifficulty = 'easy' | 'medium' | 'hard';

/**
 * Bot AI configuration
 */
export interface BotConfig {
  difficulty: BotDifficulty;
  /** Chance of making a suboptimal play (0-1) */
  errorRate: number;
}

/**
 * Default bot configurations
 */
export const BOT_CONFIGS: Record<BotDifficulty, BotConfig> = {
  easy: { difficulty: 'easy', errorRate: 0.3 },
  medium: { difficulty: 'medium', errorRate: 0.15 },
  hard: { difficulty: 'hard', errorRate: 0.05 },
};

/**
 * Bot decision result
 */
export interface BotDecision {
  action: 'attack' | 'defend' | 'take' | 'pass';
  card?: Card;
  reason: string;
}

/**
 * Selects a card to attack with
 *
 * Strategy:
 * 1. First attack: play lowest non-trump, or lowest trump if no choice
 * 2. Subsequent attacks: play lowest valid card
 */
export function botSelectAttack(
  botHand: Card[],
  playZone: PlaySlot[],
  trump: Suit,
  config: BotConfig = BOT_CONFIGS.medium
): BotDecision {
  // Get valid attack cards
  const validCards = getValidAttackCards(botHand, playZone);

  if (validCards.length === 0) {
    return { action: 'pass', reason: 'No valid attack cards' };
  }

  // Sort hand (non-trump first, then by value)
  const sorted = sortHand(validCards, trump);

  // Apply error rate for non-optimal play
  if (Math.random() < config.errorRate && sorted.length > 1) {
    // Play second-best card
    return {
      action: 'attack',
      card: sorted[1],
      reason: 'Suboptimal attack (error)',
    };
  }

  // Play lowest card
  return {
    action: 'attack',
    card: sorted[0],
    reason: 'Lowest valid card',
  };
}

/**
 * Attempts to defend all open slots
 *
 * Strategy:
 * 1. For each open slot, find lowest card that beats attacker
 * 2. If cannot defend any slot, take all cards
 * 3. Remove used cards from simulated hand
 */
export function botDefend(
  botHand: Card[],
  playZone: PlaySlot[],
  trump: Suit,
  config: BotConfig = BOT_CONFIGS.medium
): {
  defended: boolean;
  defenses: Array<{ slotIndex: number; card: Card }>;
  reason: string;
} {
  const openSlots = getOpenSlots(playZone);

  if (openSlots.length === 0) {
    return { defended: true, defenses: [], reason: 'No open slots' };
  }

  // Simulate defending
  const handCopy = [...botHand];
  const defenses: Array<{ slotIndex: number; card: Card }> = [];

  for (const slot of openSlots) {
    const slotIndex = playZone.indexOf(slot);
    const defender = findOptimalDefense(handCopy, slot.attacker, trump);

    if (!defender) {
      // Cannot defend this slot
      return {
        defended: false,
        defenses: [],
        reason: `Cannot defend against ${slot.attacker.rank}${slot.attacker.suit}`,
      };
    }

    // Apply error rate
    if (Math.random() < config.errorRate) {
      // Find a higher card to play (wasteful)
      const validDefenses = handCopy.filter(c =>
        cardBeats(slot.attacker, c, trump)
      );

      if (validDefenses.length > 1) {
        // Sort ascending and pick second lowest
        validDefenses.sort((a, b) => a.value - b.value);
        defenses.push({ slotIndex, card: validDefenses[1] });

        // Remove from simulated hand
        const idx = handCopy.findIndex(
          c => c.suit === validDefenses[1].suit && c.rank === validDefenses[1].rank
        );
        if (idx > -1) handCopy.splice(idx, 1);
        continue;
      }
    }

    defenses.push({ slotIndex, card: defender });

    // Remove from simulated hand
    const idx = handCopy.findIndex(
      c => c.suit === defender.suit && c.rank === defender.rank
    );
    if (idx > -1) handCopy.splice(idx, 1);
  }

  return {
    defended: true,
    defenses,
    reason: `Defended ${defenses.length} slot(s)`,
  };
}

/**
 * Decides whether to continue attacking or pass
 *
 * Strategy:
 * 1. Continue if have valid cards and defender seems weak (has few cards)
 * 2. Pass if running low on cards or defender has many cards
 */
export function botDecideContinueAttack(
  botHand: Card[],
  playZone: PlaySlot[],
  defenderHandSize: number,
  trump: Suit,
  config: BotConfig = BOT_CONFIGS.medium
): BotDecision {
  const validCards = getValidAttackCards(botHand, playZone);

  if (validCards.length === 0) {
    return { action: 'pass', reason: 'No valid attack cards' };
  }

  // Don't exceed 6 cards
  if (playZone.length >= 6) {
    return { action: 'pass', reason: 'Max cards reached' };
  }

  // Easy bot: randomly stop attacking
  if (config.difficulty === 'easy' && Math.random() < 0.3) {
    return { action: 'pass', reason: 'Random pass (easy)' };
  }

  // Consider defender's hand size
  if (defenderHandSize <= 2 && botHand.length > 3) {
    // Try to finish them off
    return {
      action: 'attack',
      card: sortHand(validCards, trump)[0],
      reason: 'Pressuring weak defender',
    };
  }

  // Default: continue with lowest card
  return {
    action: 'attack',
    card: sortHand(validCards, trump)[0],
    reason: 'Continuing attack',
  };
}

/**
 * Calculates bot "confidence" for UI display
 * Returns 0-1 based on hand strength relative to game state
 */
export function calculateBotConfidence(
  botHand: Card[],
  playZone: PlaySlot[],
  trump: Suit
): number {
  if (botHand.length === 0) return 0;

  // Count trump cards
  const trumpCount = botHand.filter(c => isTrump(c, trump)).length;
  const trumpRatio = trumpCount / botHand.length;

  // Count high cards (J, Q, K, A)
  const highCards = botHand.filter(c => c.value >= 5).length;
  const highRatio = highCards / botHand.length;

  // Base confidence on card strength
  let confidence = trumpRatio * 0.4 + highRatio * 0.6;

  // Reduce confidence if defending and play zone is large
  if (playZone.length > 3) {
    confidence *= 0.8;
  }

  return Math.min(1, Math.max(0, confidence));
}

/**
 * Simulates a full bot turn
 * Returns the sequence of actions
 */
export function simulateBotTurn(
  botHand: Card[],
  playZone: PlaySlot[],
  defenderHandSize: number,
  trump: Suit,
  isAttacking: boolean,
  config: BotConfig = BOT_CONFIGS.medium
): BotDecision[] {
  const actions: BotDecision[] = [];

  if (isAttacking) {
    // Attack phase
    const attack = botSelectAttack(botHand, playZone, trump, config);
    actions.push(attack);

    if (attack.action === 'attack' && attack.card) {
      // Simulate continuing attack
      let continueAttack = true;
      let simulatedZone = [...playZone, { attacker: attack.card, defender: null }];
      let simulatedHand = botHand.filter(
        c => !(c.suit === attack.card!.suit && c.rank === attack.card!.rank)
      );

      while (continueAttack && simulatedHand.length > 0) {
        const decision = botDecideContinueAttack(
          simulatedHand,
          simulatedZone,
          defenderHandSize,
          trump,
          config
        );

        if (decision.action === 'pass') {
          continueAttack = false;
        } else if (decision.action === 'attack' && decision.card) {
          actions.push(decision);
          simulatedZone = [...simulatedZone, { attacker: decision.card, defender: null }];
          simulatedHand = simulatedHand.filter(
            c => !(c.suit === decision.card!.suit && c.rank === decision.card!.rank)
          );
        }
      }

    }

    // Always terminate an attack sequence with a 'pass' marker (whether the bot
    // chose to stop or ran out of cards mid-attack).
    if (actions[actions.length - 1]?.action !== 'pass') {
      actions.push({ action: 'pass', reason: 'Done attacking' });
    }
  } else {
    // Defense phase
    const defense = botDefend(botHand, playZone, trump, config);

    if (defense.defended) {
      for (const d of defense.defenses) {
        actions.push({
          action: 'defend',
          card: d.card,
          reason: `Defending slot ${d.slotIndex}`,
        });
      }
    } else {
      actions.push({ action: 'take', reason: defense.reason });
    }
  }

  return actions;
}

/**
 * Creates a bot with persistent configuration
 */
export function createBot(difficulty: BotDifficulty = 'medium') {
  const config = BOT_CONFIGS[difficulty];

  return {
    config,
    selectAttack: (hand: Card[], zone: PlaySlot[], trump: Suit) =>
      botSelectAttack(hand, zone, trump, config),
    defend: (hand: Card[], zone: PlaySlot[], trump: Suit) =>
      botDefend(hand, zone, trump, config),
    decideContinue: (hand: Card[], zone: PlaySlot[], defenderSize: number, trump: Suit) =>
      botDecideContinueAttack(hand, zone, defenderSize, trump, config),
    calculateConfidence: (hand: Card[], zone: PlaySlot[], trump: Suit) =>
      calculateBotConfidence(hand, zone, trump),
  };
}

export type Bot = ReturnType<typeof createBot>;
